import { handleize, normalize } from "./general";

export const COLOR_NAME_MAPPINGS = {
  // Single colors
  "blue-salt": ["blue-salt", "bleuet"],
  char: ["char", "charbon"],
  chrome: ["chrome"],
  gold: ["gold"],
  sage: ["sage", "sauge"],
  spice: ["spice", "terre-cuite"],
  steam: ["steam", "creme"],
  // Combined colors
  "blue-salt-char": ["blue-salt-char", "bleuet-charbon"],
  "char-blue-salt": ["char-blue-salt", "charbon-bleuet"],
  "chrome-blue-salt": ["chrome-blue-salt", "chrome-bleuet"],
  "chrome-char": ["chrome-char", "chrome-charbon"],
  "chrome-gold": ["chrome-gold"],
  "chrome-sage": ["chrome-sage", "chrome-sauge"],
  "chrome-spice": ["chrome-spice", "chrome-terre-cuite"],
  "chrome-steam": ["chrome-steam", "chrome-creme", "chrome-cr-me"],
  "gold-blue-salt": ["gold-blue-salt", "gold-bleuet"],
  "gold-char": ["gold-char", "gold-charbon"],
  "gold-sage": ["gold-sage", "gold-sauge"],
  "gold-spice": ["gold-spice", "gold-terre-cuite"],
  "gold-steam": ["gold-steam", "gold-creme", "gold-cr-me"],
  "sage-char": ["sage-char", "sauge-charbon"],
  "sage-steam": ["sage-steam", "sauge-creme", "sauge-cr-me"],
  "spice-steam": ["spice-steam", "terre-cuite-creme", "terre-cuite-cr-me"],
  "steam-blue-salt": ["steam-blue-salt", "creme-bleuet", "cr-me-bleuet"],
  "steam-sage": ["steam-sage", "creme-sauge", "cr-me-sauge"],
  "steam-spice": ["steam-spice", "creme-terre-cuite", "cr-me-terre-cuite"],
} as const;

export type CanonicalColorName = keyof typeof COLOR_NAME_MAPPINGS;

/**
 * Maps a localized color name to its canonical one
 * @param colorName - The localized color name to map (case-insensitive)
 * @returns The canonical color name or null
 */
export const getCanonicalColorName = (
  colorName: string
): CanonicalColorName | null => {
  const normalizedColorName = colorName.toLowerCase().trim();

  for (const [canonicalName, localizedNames] of Object.entries(
    COLOR_NAME_MAPPINGS
  )) {
    if (
      localizedNames.some(
        (localizedName) => normalizedColorName === localizedName
      )
    ) {
      return canonicalName as CanonicalColorName;
    }
  }

  return null;
};

/**
 * Gets all localized names for a given canonical color name
 * @param canonicalColorName - The canonical color name
 * @returns Array of localized names for the given color name
 */
export const getLocalizedColorNames = (
  canonicalColorName: CanonicalColorName
): readonly string[] => {
  return COLOR_NAME_MAPPINGS[canonicalColorName];
};

/**
 * Gets the CSS class name for a color (for Tailwind)
 * @param colorName - The color name (localized or global)
 * @returns The CSS class name (e.g., "bg-product-char")
 */
export const getColorCssClass = (colorName: string): string => {
  const handleizedName = handleize(normalize(colorName));
  const canonicalColorName = getCanonicalColorName(handleizedName);

  return `bg-product-${canonicalColorName ?? handleizedName}`;
};
